import { useCallback, useRef, useState } from 'react';
import enviro from 'enviro';
import { matches } from 'UIComponents/utils/Dom';
import Raven from 'raven-js';
import { isSystemEntry } from '../../utils/systemFileUtils';
const isFileSystemFileEntry = item => {
  return item.isFile;
};
const isFileSystemDirectoryEntry = item => {
  return item.isDirectory;
};

// Converts a FileSystemFileEntry to a File object
// And prefixes the folder structure in the webkitRelativePath property
const convertFileEntryToFile = (item, path) => {
  return new Promise((resolve, reject) => {
    item.file(file => {
      const fileWithPath = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
      });
      Object.defineProperty(fileWithPath, 'webkitRelativePath', {
        value: `${path}${file.name}`,
        writable: false
      });
      resolve(fileWithPath);
    }, error => {
      Raven.captureMessage('Error converting file entry to file:', {
        extra: {
          error
        }
      });
      reject(error);
    });
  });
};

// Reads a single batch of entries from a directory reader
// Returns empty array when no more entries are available
const readSingleBatch = dirReader => {
  return new Promise((resolve, reject) => {
    dirReader.readEntries(entries => resolve(entries), error => reject(error));
  });
};

// Traverse the file tree, if entry is a directory, read the entries and recursively traverse the file tree
// If entry is a file, convert the file entry to a File object
const traverseFileTree = async (item, path = '') => {
  if (isFileSystemFileEntry(item)) {
    const convertedFile = await convertFileEntryToFile(item, path);
    return [convertedFile];
  }
  if (isFileSystemDirectoryEntry(item)) {
    const dirReader = item.createReader();
    const directoryPath = `${path}${item.name}/`;
    const directoryFiles = [];
    try {
      // Recursively read all batches from the directory
      // The browser returns entries in batches, so we need to call readEntries repeatedly
      // See MDN: https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryReader/readEntries
      const readDirectoryBatch = async () => {
        // 1. Read one batch from the directory
        const entriesInBatch = await readSingleBatch(dirReader);

        // 2. If no more entries, end of recursion
        if (entriesInBatch.length === 0) {
          return;
        }

        // 3. Process this batch: recursively convert each entry to File(s), skipping system files
        const filePromises = entriesInBatch.filter(entry => !isSystemEntry(entry)).map(entry => traverseFileTree(entry, directoryPath));

        // 4. Wait for all entries in this batch to be processed
        const results = await Promise.all(filePromises);
        directoryFiles.push(...results.flat());

        // 5. Recursively call to get the NEXT batch
        await readDirectoryBatch();
      };
      await readDirectoryBatch();
      return directoryFiles;
    } catch (error) {
      Raven.captureMessage('Error reading directory entries:', {
        extra: {
          error
        }
      });
      return [];
    }
  }
  return [];
};
const processFilesAndFolders = async dataTransferItemList => {
  const items = Array.from(dataTransferItemList);

  // Get all entries before processing any of them
  // Once we start processing entries, the DataTransferItem objects become invalid
  const entries = [];
  let hasFolders = false;
  for (const item of items) {
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        entries.push(entry);
        // Check if any of the top level entries is a directory for usage tracking
        if (entry.isDirectory) {
          hasFolders = true;
        }
      }
    }
  }

  // Process all entries by traversing the file tree, skipping top-level system files
  const fileArrays = await Promise.all(entries.filter(entry => !isSystemEntry(entry)).map(entry => traverseFileTree(entry)));
  return {
    files: fileArrays.flat(),
    hasFolders
  };
};
const cancelDragEvent = e => {
  e.preventDefault();
  e.stopPropagation();
};
const isDebug = () => enviro.debug('filedrop');
const logDebug = (...args) => {
  if (isDebug()) {
    console.log.apply(console, ['[FileDragAndDrop]', ...args]);
  }
};
const matchesSelector = (el, className) => {
  if (el instanceof SVGElement) {
    return false;
  }
  return matches(el, className);
};
const matchesOrWithinSelector = (el, className) => {
  if (matchesSelector(el, className)) {
    return true;
  }
  while (el && el.parentElement) {
    if (matchesSelector(el.parentElement, className)) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
};
const hasFilesInDragEvent = e => {
  var _e$dataTransfer$files;
  if (!e.dataTransfer) {
    return false;
  }
  const dataTransferTypes = e.dataTransfer.types;
  if (dataTransferTypes !== null && dataTransferTypes !== void 0 && dataTransferTypes.some(type => type === 'Files' || type === 'application/x-moz-file')) {
    return true;
  }
  return ((_e$dataTransfer$files = e.dataTransfer.files) === null || _e$dataTransfer$files === void 0 ? void 0 : _e$dataTransfer$files.length) > 0;
};

// the event handlers need to be applied to a child element in order to function
// `isOver` is optional, use if styling needs to be updated while a file has been dragged within the element
const FileDragAndDrop = ({
  children,
  disabled = false,
  ignoreTargetSelector,
  onDropFiles,
  enableFolderUpload = false
}) => {
  const [isOver, setIsOver] = useState(false);
  const isOverRef = useRef(null);
  const onDragEnter = useCallback(e => {
    if (hasFilesInDragEvent(e)) {
      if (ignoreTargetSelector && matchesOrWithinSelector(e.target, ignoreTargetSelector)) {
        logDebug('ignoring onDragEnter on target to parent classname', e.target, ignoreTargetSelector);
        return;
      } else {
        logDebug('onDragEnter', e.target);
      }
      e.preventDefault();
      isOverRef.current = e.target;
      setIsOver(true);
    } else {
      logDebug('no files onDragEnter');
    }
  }, [ignoreTargetSelector]);
  const onDragOver = useCallback(e => {
    cancelDragEvent(e);
  }, []);
  const onDragLeave = useCallback(e => {
    if (isOverRef.current === e.target) {
      isOverRef.current = null;
      setIsOver(false);
    }
  }, []);
  const onDrop = useCallback(async e => {
    if (ignoreTargetSelector && e.target instanceof Element && matchesOrWithinSelector(e.target, ignoreTargetSelector)) {
      logDebug('ignoring onDrop on target to parent classname', e.target, ignoreTargetSelector);
      return;
    } else {
      if (isDebug()) {
        e.persist();
        logDebug('onDrop', e);
      }
    }
    if (!enableFolderUpload) {
      const files = e.dataTransfer ? e.dataTransfer.files : [];
      if (files && files.length) {
        e.preventDefault();
        onDropFiles(Array.from(files));
        setIsOver(false);
      }
      return;
    }
    if (e.dataTransfer && e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      e.preventDefault();
      setIsOver(false);
      try {
        const {
          files,
          hasFolders
        } = await processFilesAndFolders(e.dataTransfer.items);
        if (files.length > 0) {
          onDropFiles(files, hasFolders);
        }
      } catch (error) {
        Raven.captureMessage('Error processing files and folders:', {
          extra: {
            error
          }
        });
      }
    }
  }, [ignoreTargetSelector, onDropFiles, enableFolderUpload]);
  if (disabled) {
    return children({
      isOver: false,
      onDragOver: cancelDragEvent,
      onDrop: cancelDragEvent
    });
  }

  // eslint-disable-next-line react-hooks/refs
  return children({
    isOver,
    onDrop: onDrop,
    onDragEnter,
    onDragOver,
    onDragLeave
  });
};
export default FileDragAndDrop;